import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  capitalizeFirst,
  fieldOrPrompt,
  listOrPrompt,
  lowerFirstWord,
  splitItems,
  splitValues,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A lab report skeleton: the IMRaD sections a science course expects, with the
 * writer's aim, hypothesis, variables, materials, method and observations placed where
 * they belong, and a blank results table built from the variables — one row per value of
 * the independent variable, one column per trial, then the mean.
 */

export interface LabReportInput {
  title: string;
  byline: string;
  aim: string;
  hypothesis: string;
  independent: string;
  dependent: string;
  /** One per line. */
  controlled: string;
  /** The values of the independent variable tested: "10, 20, 30". */
  levels: string;
  /** "1"–"5": repeats per condition. */
  trials: string;
  /** One per line. */
  materials: string;
  /** One step per line. */
  method: string;
  /** One observation per line. */
  observations: string;
}

export interface LabReportLabels {
  defaultTitle: string;
  sections: {
    abstract: string;
    introduction: string;
    methods: string;
    results: string;
    discussion: string;
    conclusion: string;
    references: string;
  };
  subheadings: {
    variables: string;
    controlled: string;
    materials: string;
    procedure: string;
    analysis: string;
  };
  fields: {
    aim: string;
    hypothesis: string;
    independent: string;
    dependent: string;
    hypothesisTested: string;
    aimToAnswer: string;
  };
  guidance: {
    abstract: string;
    introduction: string;
    methods: string;
    results: string;
    observations: string;
    figures: string;
    discussion: string;
    references: string;
  };
  prompts: {
    title: string;
    abstract: string;
    background: string;
    aim: string;
    hypothesis: string;
    independent: string;
    dependent: string;
    controlled: string;
    materials: string;
    procedure: string;
    analysis: string;
    results: string;
    figure: string;
    trend: string;
    theory: string;
    errors: string;
    compare: string;
    conclusion: string;
    references: string;
  };
  table: {
    number: string;
    caption: string;
    captionGeneric: string;
    independentHeader: string;
    dependentHeader: string;
    trial: string;
    mean: string;
  };
  fallback: { independent: string; dependent: string };
}

const BLANK_ROWS = 4;
const MAX_TRIALS = 5;

export function trialCount(raw: string): number {
  const value = Number.parseInt(raw, 10);
  if (!Number.isFinite(value) || value < 1) {
    return 1;
  }
  return Math.min(value, MAX_TRIALS);
}

/** The results table: blank cells to fill in, laid out from the writer's own variables. */
export function resultsTable(
  input: LabReportInput,
  labels: LabReportLabels,
  locale: string
): Extract<DocBlock, { kind: "table" }> {
  const independent = input.independent.trim();
  const dependent = input.dependent.trim();
  const trials = trialCount(input.trials);
  const first = independent || labels.table.independentHeader;
  const head =
    trials === 1
      ? [first, dependent || labels.table.dependentHeader]
      : [
          first,
          ...Array.from({ length: trials }, (_, index) =>
            t(labels.table.trial, { n: index + 1 })
          ),
          labels.table.mean,
        ];
  const levels = splitValues(input.levels);
  const rowLabels =
    levels.length > 0 ? levels : Array.from({ length: BLANK_ROWS }, () => "");
  const title =
    independent && dependent
      ? t(labels.table.caption, {
          dependent: capitalizeFirst(dependent, locale),
          independent: lowerFirstWord(independent, locale),
        })
      : labels.table.captionGeneric;
  return {
    kind: "table",
    caption: [{ text: labels.table.number, bold: true }, { text: ` ${title}` }],
    head,
    rows: rowLabels.map((level) => [
      level,
      ...Array.from({ length: head.length - 1 }, () => ""),
    ]),
  };
}

function introduction(
  input: LabReportInput,
  labels: LabReportLabels
): DocSection {
  return {
    heading: labels.sections.introduction,
    blocks: [
      { kind: "guidance", text: labels.guidance.introduction },
      { kind: "prompt", text: labels.prompts.background },
      fieldOrPrompt(labels.fields.aim, input.aim, labels.prompts.aim),
      fieldOrPrompt(
        labels.fields.hypothesis,
        input.hypothesis,
        labels.prompts.hypothesis
      ),
    ],
  };
}

function methods(input: LabReportInput, labels: LabReportLabels): DocSection {
  return {
    heading: labels.sections.methods,
    blocks: [
      { kind: "guidance", text: labels.guidance.methods },
      { kind: "subheading", text: labels.subheadings.variables },
      fieldOrPrompt(
        labels.fields.independent,
        input.independent,
        labels.prompts.independent
      ),
      fieldOrPrompt(
        labels.fields.dependent,
        input.dependent,
        labels.prompts.dependent
      ),
      { kind: "subheading", text: labels.subheadings.controlled },
      ...listOrPrompt(input.controlled, "bullet", labels.prompts.controlled),
      { kind: "subheading", text: labels.subheadings.materials },
      ...listOrPrompt(input.materials, "bullet", labels.prompts.materials),
      { kind: "subheading", text: labels.subheadings.procedure },
      ...listOrPrompt(input.method, "decimal", labels.prompts.procedure),
      { kind: "subheading", text: labels.subheadings.analysis },
      { kind: "prompt", text: labels.prompts.analysis },
    ],
  };
}

function results(
  input: LabReportInput,
  labels: LabReportLabels,
  locale: string
): DocSection {
  const observations = splitItems(input.observations);
  const independent = input.independent.trim() || labels.fallback.independent;
  const dependent = input.dependent.trim() || labels.fallback.dependent;
  const notes: DocBlock[] =
    observations.length > 0
      ? [
          { kind: "guidance", text: labels.guidance.observations },
          {
            kind: "list",
            list: {
              style: "bullet",
              items: observations.map((text) => ({ text })),
            },
          },
        ]
      : [{ kind: "prompt", text: labels.prompts.results }];
  return {
    heading: labels.sections.results,
    blocks: [
      { kind: "guidance", text: labels.guidance.results },
      ...notes,
      resultsTable(input, labels, locale),
      {
        kind: "prompt",
        text: t(labels.prompts.figure, {
          dependent: lowerFirstWord(dependent, locale),
          independent: lowerFirstWord(independent, locale),
        }),
      },
      { kind: "guidance", text: labels.guidance.figures },
    ],
  };
}

function discussion(
  input: LabReportInput,
  labels: LabReportLabels
): DocSection {
  const hypothesis = input.hypothesis.trim();
  const tested: DocBlock[] = hypothesis
    ? [
        {
          kind: "fields",
          fields: [
            { label: labels.fields.hypothesisTested, value: hypothesis },
          ],
        },
      ]
    : [];
  return {
    heading: labels.sections.discussion,
    blocks: [
      { kind: "guidance", text: labels.guidance.discussion },
      ...tested,
      { kind: "prompt", text: labels.prompts.trend },
      { kind: "prompt", text: labels.prompts.theory },
      { kind: "prompt", text: labels.prompts.errors },
      { kind: "prompt", text: labels.prompts.compare },
    ],
  };
}

function conclusion(
  input: LabReportInput,
  labels: LabReportLabels
): DocSection {
  const aim = input.aim.trim();
  const answer: DocBlock[] = aim
    ? [
        {
          kind: "fields",
          fields: [{ label: labels.fields.aimToAnswer, value: aim }],
        },
      ]
    : [];
  return {
    heading: labels.sections.conclusion,
    blocks: [...answer, { kind: "prompt", text: labels.prompts.conclusion }],
  };
}

export function buildLabReport(
  input: LabReportInput,
  labels: LabReportLabels,
  locale: string
): DocumentModel {
  const title = input.title.trim();
  const byline = input.byline.trim();
  const opening: DocSection[] = title
    ? []
    : [{ blocks: [{ kind: "prompt", text: labels.prompts.title }] }];
  return {
    title: title || labels.defaultTitle,
    subtitle: byline || undefined,
    sections: [
      ...opening,
      {
        heading: labels.sections.abstract,
        blocks: [
          { kind: "guidance", text: labels.guidance.abstract },
          { kind: "prompt", text: labels.prompts.abstract },
        ],
      },
      introduction(input, labels),
      methods(input, labels),
      results(input, labels, locale),
      discussion(input, labels),
      conclusion(input, labels),
      {
        heading: labels.sections.references,
        blocks: [
          { kind: "guidance", text: labels.guidance.references },
          { kind: "prompt", text: labels.prompts.references },
        ],
      },
    ],
  };
}
